<template>

<form id="userForm" class="mx-auto col-sm-8" @submit.prevent>

  <div class="row">
    <div class="form-group col-lg-6" :class="{ 'has-error': errors.has('name') }">
      <label for="name">{{ $t('name') }}</label>
      <input
        type="text"
        id="name"
        name="name"
        class="form-control"
        :placeholder="$t('type_name')"
        v-model="user.name"
        v-validate="'required'"
      >
      <small v-show="errors.has('name')" class="form-text text-muted">
        {{ errors.first('name') }}
      </small>
    </div>

    <div class="form-group col-lg-6" :class="{ 'has-error': errors.has('email') }">
      <label for="email">{{ $t('email') }}</label>
      <input
        type="email"
        class="form-control"
        id="email"
        name="email"
        :placeholder="$t('type_email')"
        v-model="user.email"
        v-validate="'required|email'"
      >
      <small v-show="errors.has('email')" class="form-text text-muted">
        {{ errors.first('email') }}
      </small>
    </div>
  </div>

  <div class="row">
    <div class="form-group col-lg-6">
      <div class="form-group">
        <label for="company">{{ $t('company') }}</label>
          <Combobox
            class="bg-white"
            name='company'
            :hasErrors="errors.has('company')"
            :placeholder="$t('select_a_item')"
            label="name"
            :options="companies"
            :initial-value="user.company"
            v-model="user.company"
            :reduce="companies => companies.id"
            v-validate="'required'"
          />
      </div>

      <small v-show="errors.has('company')" class="form-text">
        {{ errors.first('company') }}
      </small>
    </div>

    <div class="form-group col-lg-6">
      <div class="form-group">
        <label for="usertype">{{ $t('usertype') }}</label>
          <Combobox
            class="bg-white"
            name='usertype'
            :hasErrors="errors.has('usertype')"
            :placeholder="$t('select_a_item')"
            label="name"
            :options="usertypes"
            :initial-value="user.type"
            v-model="user.type"
            :reduce="type => type.id"
            v-validate="'required'"
          />
      </div>

      <small v-show="errors.has('usertype')" class="form-text">
        {{ errors.first('usertype') }}
      </small>
    </div>
  </div>

  <div class="form-group" :class="{ 'has-error': errors.has('password') }">
    <label for="password">{{ $t('password') }}</label>
    <div class="input-group">
      <input
        :type="passwordFieldType"
        class="form-control"
        id="password"
        name="password"
        :placeholder="$t('type_password')"
        v-model="user.password"
        v-validate="(!this.user.id || user.password) ? 'required|min:6|max:35' : ''"
        ref="password"
      >
      <div class="input-group-append">
        <button type="button" class="btn btn-secondary" @click="switchVisibility()">
          <span :class="[passwordFieldType === 'text' ? 'far fa-eye' : 'far fa-eye-slash']"></span>
        </button>
        <button type="button" class="btn btn-primary" @click="generate()">
          <span class="fas fa-sync-alt"></span>
        </button>
      </div>
    </div>

    <small v-show="errors.has('password')" class="form-text text-muted">
      {{ errors.first('password') }}
    </small>
  </div>

  <div class="form-group" :class="{ 'has-error': errors.has('confirm_password') }">
    <label for="confirm_password">{{ $t('confirm_password') }}</label>
    <input
      :type="passwordFieldType"
      class="form-control"
      id="confirm_password"
      name="confirm_password"
      :placeholder="$t('type_confirm_password')"
      v-model="confirmPassword"
      v-validate="(this.user.password) ? 'required|confirmed:password' : ''"
    >
    <small v-show="errors.has('confirm_password')" class="form-text text-muted">
      {{ errors.first('confirm_password') }}
    </small>
  </div>

  <div class="row">
    <div class="form-group col-6">
      <div class="switch">
        <label for="admin">{{ $t('admin') }}</label>
        <div class="onoffswitch2">
          <input
            type="checkbox"
            class="onoffswitch2-checkbox"
            id="admin"
            v-model="user.admin">
          <label class="onoffswitch2-label" for="admin">
              <span class="onoffswitch2-inner"></span>
              <span class="onoffswitch2-switch"></span>
          </label>
        </div>
      </div>
    </div>

    <div class="form-group col-6">
      <div class="switch">
        <label for="isActive">{{ $t('active') }}</label>
        <div class="onoffswitch2">
          <input
            type="checkbox"
            class="onoffswitch2-checkbox"
            id="isActive"
            v-model="user.isActive">
          <label class="onoffswitch2-label" for="isActive">
              <span class="onoffswitch2-inner"></span>
              <span class="onoffswitch2-switch"></span>
          </label>
        </div>
      </div>
    </div>
  </div>

  <div class="form-group row">
    <div class="col-sm-4 col-sm-offset-2">
      <LoaderButton @click="onClick">
        {{ $t('save') }}
      </LoaderButton>
    </div>
  </div>

</form>

</template>

<script>
import { mapState, mapActions } from 'vuex'
import LoaderButton from '@/components/button/LoaderButton'
import Combobox from '@/components/shared/ComboBox'

export default {
  name: 'UsersForm',
  components: {
    LoaderButton,
    Combobox
  },
  
  data: () => ({
    passwordFieldType: 'text',
    confirmPassword: ''
  }),
  
  props: {
    user: {
      type: Object,
      default: () => ({
        id: undefined,
        name: '',
        email: '',
        company: '',
        admin: undefined,
        password: '',
        type: '',
        isActive: true
      })
    },
    characters: {
      type: String,
      default: 'a-z,A-Z,0-9,#'
    }
  },
  
  computed: {
    ...mapState({
      companies: state => state.company.all,
      usertypes: state => state.usertypes.all
    })
  },
  
  methods: {
    ...mapActions('users', [
      'create',
      'edit',
      'cleanState'
    ]),

    switchVisibility () {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password'
    },

    customLabel (option) {
      return option.name
    },

    generate () {
      let charactersArray = this.characters.split(',')
      let CharacterSet = ''
      let password = ''

      if (charactersArray.indexOf('a-z') >= 0) {
        CharacterSet += 'abcdefghijklmnopqrstuvwxyz'
      }

      if (charactersArray.indexOf('A-Z') >= 0) {
        CharacterSet += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      }

      if (charactersArray.indexOf('0-9') >= 0) {
        CharacterSet += '0123456789'
      }

      if (charactersArray.indexOf('#') >= 0) {
        CharacterSet += '![]{}()%&*$#^<>~@|'
      }

      for (let i = 0; i < 15; i++) {
        password += CharacterSet.charAt(Math.floor(Math.random() * CharacterSet.length))
      }
      this.user.password = password
      this.confirmPassword = password
    },

    async onClick (evtClick, doneFn) {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        const err = new Error(this.$t('invalid_form_fields'))
        this.$store.dispatch('notify/error', err)
        return
      }
      const { id } = this.user
      let promise
      if (id) {
        if (this.user.password === '') {
          this.user.password = undefined
        }
        promise = this.edit({ id, data: this.user })
      } else {
        promise = this.create(this.user)
      }
      const result = await promise
      if (result) {
        this.$router.push({ name: 'listusers' })
      }
    }
  },
  
  async mounted () {
    if (!this.user.id) this.generate()

    const { dispatch } = this.$store
    await Promise.all([
      dispatch('company/fetchAll'),
      dispatch('usertypes/fetchAll')
    ])
  },
  
  destroyed () {
    const { dispatch } = this.$store

    dispatch('company/cleanState')
    dispatch('usertypes/cleanState')
    this.cleanState()
  }
}
</script>
